import React from 'react';
import { Card, CardContent, Box, Typography, IconButton, Chip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Timer as TimerIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { QuizQuestion } from '../../../types/quiz';

interface QuestionCardProps {
  question: QuizQuestion;
  index: number;
  onEdit: (question: QuizQuestion) => void;
  onDelete: (question: QuizQuestion) => void;
  isPublished?: boolean;
}

/**
 * Displays a single quiz question in a card format
 * Shows question text, number of options, and action buttons
 */
const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  onEdit,
  onDelete,
  isPublished = false,
}) => {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Question {index + 1}
            </Typography>
            <Typography variant="body1" fontWeight="medium" gutterBottom>
              {question.questionText}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {question.options?.length || 0} options
              </Typography>
              {question.timeLimitSeconds && (
                <Chip
                  icon={<TimerIcon />}
                  label={`${question.timeLimitSeconds}s`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => onEdit(question)}
              title={isPublished ? "View question" : "Edit question"}
            >
              {isPublished ? <VisibilityIcon /> : <EditIcon />}
            </IconButton>
            {!isPublished && (
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(question)}
                title="Delete question"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuestionCard;
